import { defu } from "defu";
import {
  login as sdkLogin,
  refresh as sdkRefresh,
  logout as sdkLogout,
  acceptUserInvite as sdkAcceptUserInvite,
  inviteUser as sdkInviteUser,
  passwordRequest as sdkPasswordRequest,
  passwordReset as sdkPasswordReset
} from "@directus/sdk";
import { useDirectusUsers } from "./use-directus-users.mjs";
import { useDirectusRest, useDirectusTokens, useRuntimeConfig } from "#imports";
export function useDirectusAuth(config) {
  const { mode: defaultMode } = useRuntimeConfig().public.directus.authConfig;
  const defaultConfig = {
    staticToken: false
  };
  const client = useDirectusRest(defu(config, defaultConfig));
  const {
    readMe: readMyself,
    setUser,
    user
  } = useDirectusUsers(defu(config, defaultConfig));
  const {
    refreshToken: refreshTokenCookie,
    set: setTokens,
    tokens
  } = useDirectusTokens(config?.staticToken ?? defaultConfig.staticToken);
  async function login(identifier, password, {
    options,
    updateStates,
    updateTokens,
    readMe
  } = {}) {
    return await client.request(sdkLogin(identifier, password, defu(options, { mode: defaultMode }))).then(async (authResponse) => {
      if (authResponse && updateStates !== false) {
        if (updateTokens !== false) {
          await setTokens(authResponse);
        }
        if (readMe !== false) {
          await readMyself(readMe);
        }
      }
      return authResponse;
    });
  }
  async function refresh({
    mode,
    refreshToken,
    updateStates,
    updateTokens,
    readMe
  } = {}) {
    const token = refreshToken ?? tokens.value?.refresh_token ?? refreshTokenCookie().value ?? void 0;
    if (!token && mode === "json") {
      throw new Error("No refresh token found.");
    }
    const authResponse = await client.request(sdkRefresh(mode ?? defaultMode, token));
    if (updateStates !== false) {
      if (updateTokens !== false) {
        await setTokens(authResponse ?? null);
      }
      if (readMe !== false) {
        await readMyself(readMe);
      }
    }
    return authResponse;
  }
  async function logout({
    refreshToken,
    mode
  } = {
    mode: defaultMode
  }) {
    const token = refreshToken ?? tokens.value?.refresh_token ?? refreshTokenCookie().value ?? void 0;
    if (!token && mode === "json") {
      throw new Error("No refresh token found.");
    }
    return await client.request(sdkLogout(token, mode)).then(() => {
      user.value = void 0;
      setTokens(null);
    });
  }
  async function passwordRequest(email, resetUrl) {
    return await client.request(sdkPasswordRequest(email, resetUrl));
  }
  async function passwordReset(token, password) {
    return await client.request(sdkPasswordReset(token, password));
  }
  async function inviteUser(email, role, inviteUrl) {
    return await client.request(sdkInviteUser(email, role, inviteUrl));
  }
  async function acceptUserInvite(token, password) {
    return await client.request(sdkAcceptUserInvite(token, password));
  }
  return {
    client,
    acceptUserInvite,
    inviteUser,
    login,
    logout,
    passwordRequest,
    passwordReset,
    refresh,
    refreshTokenCookie,
    readMe: readMyself,
    setUser,
    setTokens,
    tokens,
    user
  };
}
